// Phase 10 (design rev.5 §5) — cross-zone explicit save confirmation helpers.
//
// TRUTHFULNESS INVARIANT (paramount): the zone shown in the confirm is ALWAYS
// the SERVER-resolved IANA value — on create the Phase-7 `resolvedTimeZone`
// query (which returns exactly what EventCreate stores when no zone is picked,
// via the shared Events::ResolvedTimeZone.for_selection), on edit the Phase-10b
// `editResolvedTimeZone` query (which delegates to the SAME
// Events::ResolvedLeadZone EventUpdate#resolve uses == what EventUpdate stores:
// venue-resolved for a geocoded venue, the persisted time_zone column for a
// no/blank-geo-venue event per the Phase-9b LOCKSTEP). This module NEVER resolves a
// zone client-side — Places::TimezoneFor is offline Ruby; the browser cannot
// replicate it. It only (a) maps the authoritative IANA string to a friendly
// DISPLAY label and (b) decides whether the explicit confirm must be shown.

// IANA -> friendly label for the US zones the app actually uses (the
// Places::TimezoneFor US_STATE_TIMEZONES / STATE_CITY_OVERRIDES set). The map
// is intentionally small (YAGNI): zones outside it fall back to the raw IANA
// string so an unmapped zone is still shown TRUTHFULLY and never blocks submit.
const FRIENDLY_LABELS: Readonly<Record<string, string>> = {
  // Eastern
  'America/New_York': 'Eastern Time (ET)',
  'America/Detroit': 'Eastern Time (ET)',
  'America/Indiana/Indianapolis': 'Eastern Time (ET)',
  // Central
  'America/Chicago': 'Central Time (CT)',
  'America/Indiana/Vincennes': 'Central Time (CT)',
  // Mountain (America/Phoenix has no DST but is labelled Mountain — Arizona)
  'America/Denver': 'Mountain Time (MT)',
  'America/Boise': 'Mountain Time (MT)',
  'America/Phoenix': 'Mountain Time (MT)',
  // Pacific
  'America/Los_Angeles': 'Pacific Time (PT)',
  // Alaska
  'America/Anchorage': 'Alaska Time (AKT)',
  // Hawaii
  'Pacific/Honolulu': 'Hawaii Time (HT)',
  // Atlantic (US territories)
  'America/Puerto_Rico': 'Atlantic Time (AT)',
  'America/St_Thomas': 'Atlantic Time (AT)',
  // Chamorro (Guam)
  'Pacific/Guam': 'Chamorro Time (ChT)',
}

// Friendly label for a server-resolved IANA zone. Falls back to the raw IANA
// string when unmapped (truthful, never blocks submit) and to '' for a
// blank/nullish input (so callers never render "undefined").
export const friendlyTimeZoneLabel = (ianaZone?: string | null): string => {
  if (!ianaZone) {
    return ''
  }
  return FRIENDLY_LABELS[ianaZone] ?? ianaZone
}

type ShouldConfirmArgs = {
  // Intl.DateTimeFormat().resolvedOptions().timeZone — the editor's browser zone.
  browserZone?: string | null
  // The SERVER-resolved zone (create: resolvedTimeZone query result; edit:
  // editResolvedTimeZone query result). Pass null/'' when nothing has been resolved yet
  // (no venue AND no community selected on create) so the no-selection footgun
  // does NOT fire a spurious confirm — see below.
  resolvedZone?: string | null
}

// Decide whether the explicit cross-zone confirm must be shown before submit.
//
// 🔴 NO-SELECTION FOOTGUN (BINDING, design §5 / query_type.rb PHASE 10
// HANDOFF): the Phase-7 `resolvedTimeZone` query with NEITHER placeId NOR
// primaryCommunityId returns DEFAULT_TIMEZONE ("America/New_York") — a
// resolved-LOOKING value, NOT nil. So the gate must NOT be a bare
// `browserZone !== resolvedZone`. The caller only queries + supplies
// `resolvedZone` once a venue OR community is actually selected; until then it
// passes null/'' and this returns false (no confirm) — proven for a
// non-Eastern browser by the unit test. We also fail-OPEN when the browser
// zone is unknown: we cannot truthfully assert a mismatch, so we do not gate.
//
// Comparison is on the raw IANA strings (NOT the friendly labels): the
// server-resolved IANA is authoritative, so two distinct ids that share a
// friendly label (America/Detroit vs America/New_York) still count as a
// mismatch — the confirm must state exactly what will be stored.
export const shouldConfirmCrossZone = ({ browserZone, resolvedZone }: ShouldConfirmArgs): boolean => {
  if (!resolvedZone) {
    return false
  }
  if (!browserZone) {
    return false
  }
  return browserZone !== resolvedZone
}

// 🔴 The EXACT no-selection guard (BINDING). On the CREATE path the Phase-7
// `resolvedTimeZone` query must fire ONLY once a venue OR community is
// actually selected. With NEITHER id the query returns DEFAULT_TIMEZONE
// ("America/New_York") — a resolved-LOOKING value, NOT nil — so issuing it
// pre-selection would feed shouldConfirmCrossZone a real-looking zone and pop
// a spurious "Save as Eastern?" on every fresh form for non-Eastern browsers.
// Gating the QUERY itself (not just the comparison) is the mechanism that
// suppresses that. Pure + jest-tested so the footgun is provably handled.
// #898 item 1: a typed (not-in-directory) temp venue has no placeId, but its
// state is enough for the server to resolve a zone, so the query must fire on it
// too (else a typed cross-zone venue with no community gets no confirm).
export const shouldQueryResolvedZone = (
  placeId?: string | null,
  primaryCommunityId?: string | null,
  tempPlaceAddressState?: string | null,
): boolean => Boolean(placeId) || Boolean(primaryCommunityId) || Boolean(tempPlaceAddressState)

// Extracts the typed temp-venue state/city from an <AutoForm> onChange under the
// `tempPlace` field and applies them via the setters WITHOUT clobbering the
// sibling field (#898 item 1). GeocodeAddressFields sets the whole address
// object (key `tempPlace.address`); a manual edit sets a single leaf
// (`tempPlace.address.state`). Used by EventNew/EventEdit to drive the
// cross-zone confirm for typed (not-in-directory) venues.
export const applyTempPlaceChange = (
  key: string,
  value: unknown,
  setState: (v: string | null) => void,
  setCity: (v: string | null) => void,
): void => {
  const leaf = key.split('.').pop()
  const nested = value as { address?: Record<string, unknown> } | null
  const obj = (nested?.address ?? value) as Record<string, unknown> | null
  if (obj && typeof obj === 'object') {
    setState((obj.state as string) || null)
    setCity((obj.city as string) || null)
  } else if (leaf === 'state') {
    setState((value as string) || null)
  } else if (leaf === 'city') {
    setCity((value as string) || null)
  }
}

// The editor's current browser IANA zone, or '' when Intl is unavailable /
// returns nothing (callers then fail-open via shouldConfirmCrossZone).
export const browserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    return ''
  }
}

// #898 item 2 (validated design 2026-06-03) — the structured content of the
// on-form Summary's zone notice. Pure (no JSX, no React) so the label text,
// the no-selection gating, and the cross-zone ESCALATION are all jest-testable;
// the component just renders these pieces.
//
//   * Returns null until a zone has actually been resolved (the SAME
//     no-selection gate as the submit confirm — `resolvedZone` is null until a
//     community OR venue selection drove the query upstream), so we never show
//     a misleading default-Eastern to someone who has picked nothing.
//   * `crossZone` reuses shouldConfirmCrossZone, so the pre-warning fires on
//     EXACTLY the condition that pops the submit-time modal — one source of
//     truth, no drift. When the browser zone is unknown we fail open (no
//     escalation) just like the gate.
//   * `browserLabel` is only meaningful when `crossZone` is true (the editor's
//     own zone, for "…your computer is set to Central Time").
//
// Both labels are friendly DISPLAY strings; the underlying comparison is on the
// authoritative IANA values (inside shouldConfirmCrossZone), so two ids sharing
// a label (America/Detroit vs America/New_York) still escalate truthfully.
export type ResolvedZoneNotice = {
  zoneLabel: string
  crossZone: boolean
  browserLabel: string
}

export const resolvedZoneNotice = ({
  resolvedZone,
  browserZone,
}: {
  resolvedZone?: string | null
  browserZone?: string | null
}): ResolvedZoneNotice | null => {
  const zoneLabel = friendlyTimeZoneLabel(resolvedZone)
  if (!zoneLabel) {
    return null
  }
  const crossZone = shouldConfirmCrossZone({ browserZone, resolvedZone })
  return {
    zoneLabel,
    crossZone,
    browserLabel: crossZone ? friendlyTimeZoneLabel(browserZone) : '',
  }
}
